// Ready-made sources. Presets need no account and run on our server.
// Recipes are templates for a plain web API read with the key the user saved
// once for that service (shared/keyServices.ts): the board fills in the
// address and where the number lives, and adds the key as the request goes out.

import type { Extract, WidgetType } from "./types";

export interface ParamSpec {
  name: string;
  label: string;
  placeholder?: string;
  kind?: "text" | "number" | "select" | "textarea";
  options?: { value: string; label: string }[];
  default?: string | number;
  required?: boolean;
  help?: string;
  /** filled from what the saved key reads (a Lemon Squeezy store), so it is asked for only when there is a choice */
  account?: boolean;
}

export interface PresetSpec {
  id: string;
  label: string;
  blurb: string;
  /** no longer offered, in Connect or to agents: the sources made with it go on reading */
  retired?: boolean;
  icon: string; // lucide icon name, drawn by the app
  widget: WidgetType;
  /** the widget's size when the source is added, when its own default would not show it well */
  size?: [number, number];
  every: number; // seconds
  params: ParamSpec[];
  title: (p: Record<string, string>) => string;
  streamKey: (p: Record<string, string>) => string;
}

const key = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);

/** The papers and broadcasters News headlines reads, by their own public feeds, each tested from the board's server (13 Oct 2026). */
export const NEWS_FEEDS: { value: string; label: string; url: string }[] = [
  { value: "bbc", label: "BBC News", url: "https://feeds.bbci.co.uk/news/rss.xml" },
  { value: "bbc-world", label: "BBC News: World", url: "https://feeds.bbci.co.uk/news/world/rss.xml" },
  { value: "bbc-business", label: "BBC News: Business", url: "https://feeds.bbci.co.uk/news/business/rss.xml" },
  { value: "bbc-tech", label: "BBC News: Technology", url: "https://feeds.bbci.co.uk/news/technology/rss.xml" },
  { value: "guardian-uk", label: "The Guardian: UK", url: "https://www.theguardian.com/uk/rss" },
  { value: "guardian-world", label: "The Guardian: World", url: "https://www.theguardian.com/world/rss" },
  { value: "guardian-us", label: "The Guardian: US", url: "https://www.theguardian.com/us-news/rss" },
  { value: "sky", label: "Sky News", url: "https://feeds.skynews.com/feeds/rss/home.xml" },
  { value: "npr", label: "NPR", url: "https://feeds.npr.org/1001/rss.xml" },
  { value: "aljazeera", label: "Al Jazeera", url: "https://www.aljazeera.com/xml/rss/all.xml" },
  { value: "abc-au", label: "ABC News (Australia)", url: "https://www.abc.net.au/news/feed/2942460/rss.xml" },
  { value: "dw", label: "DW", url: "https://rss.dw.com/rdf/rss-en-all" },
  { value: "france24", label: "France 24", url: "https://www.france24.com/en/rss" },
  { value: "techcrunch", label: "TechCrunch", url: "https://techcrunch.com/feed/" },
  { value: "verge", label: "The Verge", url: "https://www.theverge.com/rss/index.xml" },
  { value: "ars", label: "Ars Technica", url: "https://feeds.arstechnica.com/arstechnica/index" },
];

/** Where Public holidays are for: a country, or a nation or state whose holidays differ (CODE or CODE-STATE, as date-holidays names them). */
export const HOLIDAY_PLACES: { value: string; label: string }[] = [
  { value: "GB-ENG", label: "England" },
  { value: "GB-SCT", label: "Scotland" },
  { value: "GB-WLS", label: "Wales" },
  { value: "GB-NIR", label: "Northern Ireland" },
  { value: "IE", label: "Ireland" },
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "AU", label: "Australia" },
  { value: "NZ", label: "New Zealand" },
  { value: "AR", label: "Argentina" },
  { value: "AT", label: "Austria" },
  { value: "BE", label: "Belgium" },
  { value: "BR", label: "Brazil" },
  { value: "CL", label: "Chile" },
  { value: "CN", label: "China" },
  { value: "CO", label: "Colombia" },
  { value: "CZ", label: "Czechia" },
  { value: "DK", label: "Denmark" },
  { value: "EG", label: "Egypt" },
  { value: "FI", label: "Finland" },
  { value: "FR", label: "France" },
  { value: "DE", label: "Germany" },
  { value: "GR", label: "Greece" },
  { value: "HK", label: "Hong Kong" },
  { value: "IN", label: "India" },
  { value: "ID", label: "Indonesia" },
  { value: "IL", label: "Israel" },
  { value: "IT", label: "Italy" },
  { value: "JP", label: "Japan" },
  { value: "KE", label: "Kenya" },
  { value: "MY", label: "Malaysia" },
  { value: "MX", label: "Mexico" },
  { value: "MA", label: "Morocco" },
  { value: "NL", label: "Netherlands" },
  { value: "NG", label: "Nigeria" },
  { value: "NO", label: "Norway" },
  { value: "PH", label: "Philippines" },
  { value: "PL", label: "Poland" },
  { value: "PT", label: "Portugal" },
  { value: "SG", label: "Singapore" },
  { value: "ZA", label: "South Africa" },
  { value: "KR", label: "South Korea" },
  { value: "ES", label: "Spain" },
  { value: "SE", label: "Sweden" },
  { value: "CH", label: "Switzerland" },
  { value: "TR", label: "Türkiye" },
  { value: "AE", label: "United Arab Emirates" },
];

export const PRESETS: PresetSpec[] = [
  {
    id: "weather",
    label: "Weather",
    blurb: "Current conditions and five days ahead, from Open-Meteo.",
    icon: "cloud-sun",
    widget: "weather",
    every: 1800,
    params: [{ name: "place", label: "Place", placeholder: "Edinburgh", required: true }],
    title: (p) => p.place || "Weather",
    streamKey: (p) => "weather-" + key(p.place || "here"),
  },
  {
    id: "crypto",
    label: "Crypto prices",
    blurb: "Live prices and the 24 hour move, from CoinGecko.",
    icon: "bitcoin",
    widget: "list",
    every: 300,
    params: [
      { name: "coins", label: "Coins", placeholder: "bitcoin, ethereum, solana", default: "bitcoin, ethereum, solana", required: true, help: "CoinGecko ids, separated by commas" },
      { name: "vs", label: "Currency", default: "usd", kind: "select", options: [{ value: "usd", label: "USD" }, { value: "eur", label: "EUR" }, { value: "gbp", label: "GBP" }] },
    ],
    title: () => "Crypto",
    streamKey: (p) => "crypto-" + key(p.coins || "prices").slice(0, 32),
  },
  {
    id: "github",
    label: "GitHub repo",
    blurb: "Stars on any public repository, with open issues and forks.",
    icon: "github",
    widget: "metric",
    every: 1800,
    params: [{ name: "repo", label: "Repository", placeholder: "owner/name", required: true }],
    title: (p) => (p.repo ? p.repo.split("/").pop() + " stars" : "GitHub stars"),
    streamKey: (p) => "github-" + key(p.repo || "repo"),
  },
  {
    id: "npm",
    label: "npm downloads",
    blurb: "Weekly downloads for any npm package.",
    icon: "package",
    widget: "metric",
    every: 3600,
    params: [{ name: "package", label: "Package", placeholder: "react", required: true }],
    title: (p) => (p.package ? `${p.package} downloads` : "npm downloads"),
    streamKey: (p) => "npm-" + key(p.package || "package"),
  },
  {
    id: "hn",
    label: "Hacker News",
    blurb: "The front page, as it moves.",
    icon: "newspaper",
    widget: "feed",
    every: 1800,
    params: [
      {
        name: "list",
        label: "List",
        kind: "select",
        default: "top",
        options: [
          { value: "top", label: "Front page" },
          { value: "best", label: "Best" },
          { value: "new", label: "Newest" },
          { value: "show", label: "Show HN" },
          { value: "ask", label: "Ask HN" },
        ],
      },
    ],
    title: (p) => (p.list === "show" ? "Show HN" : p.list === "ask" ? "Ask HN" : "Hacker News"),
    streamKey: (p) => "hn-" + (p.list || "top"),
  },
  {
    id: "status",
    // 15 Oct 2026, the widget audit: its status widget drew nothing from it, sites that turn bots away read as down,
    // and it checked only while the board was open, so it was no monitor
    retired: true,
    label: "Website status",
    blurb: "Checks that your sites answer, and how fast.",
    icon: "activity",
    widget: "status",
    every: 300,
    params: [{ name: "urls", label: "Addresses", kind: "textarea", placeholder: "https://example.com\nhttps://api.example.com/health", required: true, help: "One per line, up to 8" }],
    title: () => "Uptime",
    streamKey: (p) => "status-" + key((p.urls || "sites").split(/\s+/)[0].replace(/^https?:\/\//, "")).slice(0, 32),
  },
  {
    id: "bluesky",
    // David, 13 Oct 2026: not a thing people check
    retired: true,
    label: "Bluesky followers",
    blurb: "Follower count for any Bluesky handle.",
    icon: "at-sign",
    widget: "metric",
    every: 3600,
    params: [{ name: "handle", label: "Handle", placeholder: "you.bsky.social", required: true }],
    title: (p) => (p.handle ? `@${p.handle.replace(/^@/, "")}` : "Bluesky"),
    streamKey: (p) => "bsky-" + key(p.handle || "handle"),
  },
  {
    id: "fx",
    label: "Exchange rates",
    blurb: "Currency rates from the European Central Bank.",
    icon: "banknote",
    widget: "list",
    every: 21600,
    params: [
      { name: "base", label: "From", default: "USD", placeholder: "USD" },
      { name: "to", label: "To", default: "GBP, EUR, JPY", placeholder: "GBP, EUR" },
    ],
    title: (p) => `${(p.base || "USD").toUpperCase()} rates`,
    streamKey: (p) => "fx-" + key(p.base || "usd"),
  },
  {
    id: "earthquakes",
    // 15 Oct 2026, the widget audit: not a thing a founder checks (as Bluesky, 13 Oct); it was there to fill the map
    retired: true,
    label: "Earthquakes",
    blurb: "Quakes above 2.5 in the last day, from the USGS.",
    icon: "globe",
    widget: "map",
    every: 1800,
    params: [],
    title: () => "Earthquakes today",
    streamKey: () => "usgs-quakes",
  },
  {
    id: "news",
    label: "News headlines",
    blurb: "Top stories from a paper or broadcaster.",
    icon: "newspaper",
    widget: "feed",
    every: 1800,
    params: [{ name: "source", label: "From", kind: "select", default: "bbc", options: NEWS_FEEDS.map(({ value, label }) => ({ value, label })) }],
    title: (p) => NEWS_FEEDS.find((f) => f.value === p.source)?.label ?? "News",
    streamKey: (p) => "news-" + key(p.source || "bbc"),
  },
  {
    id: "sun",
    label: "Sun and moon",
    blurb: "Sunrise, sunset, daylight and the moon, for a place.",
    icon: "sunrise",
    widget: "sun",
    // worked out on the board, so this costs nothing; often enough to keep the day and the moon right
    every: 3600,
    params: [{ name: "place", label: "Place", placeholder: "Edinburgh", required: true }],
    title: (p) => p.place || "Sun and moon",
    streamKey: (p) => "sun-" + key(p.place || "here"),
  },
  {
    id: "holidays",
    label: "Public holidays",
    blurb: "The next public and bank holidays.",
    icon: "calendar-days",
    widget: "calendar",
    // wide enough for the next holidays beside the month, by name
    size: [576, 312],
    every: 86_400,
    params: [{ name: "country", label: "Where", kind: "select", default: "GB-ENG", options: HOLIDAY_PLACES }],
    title: (p) => `Holidays: ${HOLIDAY_PLACES.find((h) => h.value === p.country)?.label ?? p.country}`,
    streamKey: (p) => "holidays-" + key(p.country || "gb-eng"),
  },
];

export const PRESET: Record<string, PresetSpec> = Object.fromEntries(PRESETS.map((p) => [p.id, p]));

// ---------------------------------------------------------------------------
// Recipes. Built from each provider's public API documentation. They are
// marked "from the docs" in the app until someone has run one for real. None
// holds a key: each names the service whose saved key the board reads it with.

export interface RecipeSpec {
  id: string;
  label: string;
  provider: string;
  /** the service whose saved key it reads with, from shared/keyServices.ts */
  service: string;
  blurb: string;
  widget: WidgetType;
  url: string; // may contain {param} and {{today}} style tokens, never the key
  headers: { name: string; value: string; secret: false }[];
  params: ParamSpec[];
  extract: Extract;
  /** an answer in pages: where the next page's address is in each one */
  pages?: { next: string; max: number };
  format?: { style: "currency" | "number" | "percent"; currency?: string };
  /** where the answer names its own currency, so the symbol matches the account */
  currencyPath?: string;
  every: number;
  docs: string;
  title: string;
  /** the stream it fills unless asked for another */
  stream?: string;
  /** a read made of several of the service's lists, joined into one table by the board (worker/pulls.ts); url is the first */
  compose?: string;
  /** for an agent: when this is the one to use */
  use?: string;
  /** left out of Connect's choices, where a newer read does its job better; agents still see it, and its sources go on reading */
  quiet?: boolean;
  /** a widget's settings the read is best shown with, when it is added as a widget of its own */
  props?: Record<string, unknown>;
}

export const RECIPES: RecipeSpec[] = [
  {
    id: "stripe-balance",
    label: "Available balance",
    provider: "Stripe",
    service: "stripe",
    blurb: "The money Stripe can pay out to you now.",
    widget: "metric",
    url: "https://api.stripe.com/v1/balance",
    headers: [],
    params: [],
    extract: { mode: "value", path: "available[0].amount", scale: 0.01 },
    format: { style: "currency", currency: "USD" },
    currencyPath: "available[0].currency",
    every: 1800,
    docs: "https://docs.stripe.com/api/balance/balance_retrieve",
    title: "Stripe balance",
  },
  {
    id: "stripe-mrr",
    label: "MRR",
    provider: "Stripe",
    service: "stripe",
    blurb: "Monthly recurring revenue: your active subscriptions at their prices, a yearly plan counted as a twelfth.",
    widget: "metric",
    // the first of the two lists it reads: active subscriptions, then those past due, which Stripe is still collecting
    url: "https://api.stripe.com/v1/subscriptions?status=active&limit=100",
    headers: [],
    params: [],
    extract: { mode: "value", path: "value" },
    format: { style: "currency", currency: "USD" },
    currencyPath: "currency",
    compose: "stripe-mrr",
    every: 3600,
    docs: "https://docs.stripe.com/api/subscriptions/list",
    title: "MRR",
    stream: "mrr",
    use: "Monthly recurring revenue as one number. Each read adds a point, so a chart or a goal on the same stream shows it over time.",
  },
  {
    id: "stripe-revenue",
    label: "Revenue",
    provider: "Stripe",
    service: "stripe",
    blurb: "What your customers paid, less refunds, day by day, before Stripe's fees.",
    widget: "chart",
    // the first page of what it reads: the balance's transactions since the start of the period before, of which it adds up charges and refunds
    url: "https://api.stripe.com/v1/balance_transactions?limit=100",
    headers: [],
    params: [],
    extract: { mode: "value", path: "value" },
    format: { style: "currency", currency: "USD" },
    currencyPath: "currency",
    compose: "stripe-revenue",
    every: 1800,
    docs: "https://docs.stripe.com/api/balance_transactions/list",
    title: "Revenue",
    stream: "stripe-revenue",
    use: "Revenue over time: charges less refunds, for the widget's period (28 days to start; the widget switches it, this month too), each day, and the period before. Reads the balance's transactions.",
  },
  {
    id: "stripe-payment-count",
    label: "Payments",
    provider: "Stripe",
    service: "stripe",
    blurb: "How many payments you took, day by day.",
    widget: "chart",
    url: "https://api.stripe.com/v1/balance_transactions?limit=100",
    headers: [],
    params: [],
    extract: { mode: "value", path: "value" },
    compose: "stripe-payment-count",
    every: 1800,
    docs: "https://docs.stripe.com/api/balance_transactions/list",
    title: "Payments",
    stream: "stripe-payments",
    props: { chart: "bar" },
    use: "How many charges, for the widget's period with each day and the period before. A goal of sales on Stripe reads this.",
  },
  {
    id: "lemonsqueezy-30d",
    label: "Revenue, last 30 days",
    provider: "Lemon Squeezy",
    service: "lemonsqueezy",
    blurb: "Your store's thirty day revenue.",
    widget: "metric",
    url: "https://api.lemonsqueezy.com/v1/stores/{store}",
    headers: [],
    params: [{ name: "store", label: "Store", placeholder: "12345", required: true, account: true }],
    // Lemon Squeezy gives a store's revenue in US cents, whatever the store's own currency
    extract: { mode: "value", path: "data.attributes.thirty_day_revenue", scale: 0.01 },
    format: { style: "currency", currency: "USD" },
    every: 1800,
    docs: "https://docs.lemonsqueezy.com/api/stores/the-store-object",
    title: "Revenue (30d)",
    use: "One number, the last 30 days in total, never revenue by day.",
    // Revenue, read for any period with its days, took its place in Connect on 15 Oct 2026
    quiet: true,
  },
  {
    id: "lemonsqueezy-order-count",
    label: "Orders",
    provider: "Lemon Squeezy",
    service: "lemonsqueezy",
    blurb: "How many orders your store took, day by day, refunds left out.",
    widget: "chart",
    // the first of what it reads: the store's orders, newest first, back to the start of the period before
    url: "https://api.lemonsqueezy.com/v1/orders?filter[store_id]={store}&page[size]=100",
    headers: [],
    params: [{ name: "store", label: "Store", placeholder: "12345", required: true, account: true }],
    extract: { mode: "value", path: "value" },
    compose: "lemonsqueezy-order-count",
    every: 1800,
    docs: "https://docs.lemonsqueezy.com/api/orders/list-all-orders",
    title: "Orders",
    stream: "orders",
    props: { chart: "bar" },
    use: "How many orders, one number for the widget's period (28 days to start; the widget switches it, this month too) with its days and the period before. A goal of orders reads this.",
  },
  {
    id: "lemonsqueezy-revenue",
    label: "Revenue",
    provider: "Lemon Squeezy",
    service: "lemonsqueezy",
    blurb: "What your store took: orders and renewals, less refunds, day by day.",
    widget: "chart",
    // the first of the two lists it reads: orders, then subscription invoices
    url: "https://api.lemonsqueezy.com/v1/orders?filter[store_id]={store}&page[size]=100",
    headers: [],
    params: [{ name: "store", label: "Store", placeholder: "12345", required: true, account: true }],
    extract: { mode: "value", path: "value" },
    format: { style: "currency", currency: "USD" },
    compose: "lemonsqueezy-revenue",
    every: 1800,
    docs: "https://docs.lemonsqueezy.com/api/subscription-invoices/list-all-subscription-invoices",
    title: "Revenue",
    stream: "revenue",
    use: "Revenue over time: one total for the widget's period (28 days to start; the widget switches it, this month too), each day, and the period before. Orders and renewals, each once, less refunds.",
  },
  {
    id: "lemonsqueezy-mrr",
    label: "MRR",
    provider: "Lemon Squeezy",
    service: "lemonsqueezy",
    blurb: "Monthly recurring revenue: your active subscriptions at their plan prices, a yearly plan counted as a twelfth.",
    widget: "metric",
    // the first of what it reads: the store (for its currency), its active and past due subscriptions, and each plan's price
    url: "https://api.lemonsqueezy.com/v1/stores/{store}",
    headers: [],
    params: [{ name: "store", label: "Store", placeholder: "12345", required: true, account: true }],
    extract: { mode: "value", path: "value" },
    format: { style: "currency", currency: "USD" },
    currencyPath: "currency",
    compose: "lemonsqueezy-mrr",
    every: 3600,
    docs: "https://docs.lemonsqueezy.com/api/subscriptions/list-all-subscriptions",
    title: "MRR",
    stream: "mrr",
    use: "Monthly recurring revenue as one number. Each read adds a point, so a chart or a goal on the same stream shows it over time.",
  },
  {
    id: "lemonsqueezy-payments",
    label: "Payments (orders and renewals)",
    provider: "Lemon Squeezy",
    service: "lemonsqueezy",
    blurb: "Every payment, newest first: orders and subscription renewals, with what sold. Revenue over time, day by day.",
    widget: "table",
    // the first of the three lists it reads: orders, subscription invoices, and the subscriptions renewals name
    url: "https://api.lemonsqueezy.com/v1/orders?filter[store_id]={store}&page[size]=100",
    headers: [],
    params: [{ name: "store", label: "Store", placeholder: "12345", required: true, account: true }],
    // the columns the board makes; no customer names or emails
    extract: {
      mode: "table",
      columns: ["date", "kind", "product", "variant", "total", "currency", "status", "refunded"].map((name) => ({ name, path: name })),
    },
    compose: "lemonsqueezy-payments",
    every: 3600,
    docs: "https://docs.lemonsqueezy.com/api/subscription-invoices/list-all-subscription-invoices",
    title: "Payments",
    stream: "lemonsqueezy-payments",
    use: "Each payment as a row. For revenue as a number over time, lemonsqueezy-revenue adds it up. To add it up yourself: total by day over the rows whose status is paid or partial_refund. On a partial_refund row, total is what the store kept after the refund. Each row is an order, a subscription renewal or a subscription update (kind), counted once.",
  },
  {
    id: "lemonsqueezy-orders",
    label: "Order list",
    provider: "Lemon Squeezy",
    service: "lemonsqueezy",
    blurb: "Each order, newest first: when, what sold, the total and whether it was refunded.",
    widget: "table",
    // newest first is Lemon Squeezy's own order; a page holds 100 at most
    url: "https://api.lemonsqueezy.com/v1/orders?filter[store_id]={store}&page[size]=100",
    headers: [],
    params: [{ name: "store", label: "Store", placeholder: "12345", required: true, account: true }],
    // no customer names or emails: only what sold, when and for how much
    extract: {
      mode: "table",
      path: "data",
      columns: [
        { name: "date", path: "attributes.created_at" },
        { name: "order", path: "attributes.order_number" },
        { name: "product", path: "attributes.first_order_item.product_name" },
        { name: "variant", path: "attributes.first_order_item.variant_name" },
        // the order's total, in cents of its own currency
        { name: "total", path: "attributes.total", scale: 0.01 },
        { name: "currency", path: "attributes.currency" },
        { name: "status", path: "attributes.status" },
        { name: "refunded", path: "attributes.refunded" },
      ],
    },
    pages: { next: "links.next", max: 5 },
    every: 1800,
    docs: "https://docs.lemonsqueezy.com/api/orders/list-all-orders",
    title: "Orders",
    stream: "lemonsqueezy-orders",
    use: "Orders only: subscription renewals are not in it, so it is not the store's revenue.",
  },
  {
    id: "plausible-visitors",
    label: "Visitors, last 30 days",
    provider: "Plausible",
    service: "plausible",
    blurb: "Unique visitors to one of your sites.",
    widget: "metric",
    url: "https://plausible.io/api/v1/stats/aggregate?site_id={site}&period=30d&metrics=visitors",
    headers: [],
    params: [{ name: "site", label: "Site", placeholder: "example.com", required: true, account: true }],
    extract: { mode: "value", path: "results.visitors.value" },
    every: 1800,
    docs: "https://plausible.io/docs/stats-api-v1",
    title: "Visitors (30d)",
    stream: "visitors",
  },
  {
    id: "plausible-today",
    label: "Visitors today",
    provider: "Plausible",
    service: "plausible",
    blurb: "Unique visitors to one of your sites so far today.",
    widget: "metric",
    url: "https://plausible.io/api/v1/stats/aggregate?site_id={site}&period=day&metrics=visitors",
    headers: [],
    params: [{ name: "site", label: "Site", placeholder: "example.com", required: true, account: true }],
    extract: { mode: "value", path: "results.visitors.value" },
    every: 1800,
    docs: "https://plausible.io/docs/stats-api-v1",
    title: "Visitors today",
    stream: "visitors-today",
  },
  {
    id: "plausible-pages",
    label: "Top pages, last 30 days",
    provider: "Plausible",
    service: "plausible",
    blurb: "Your site's most visited pages, with their visitors.",
    widget: "list",
    url: "https://plausible.io/api/v1/stats/breakdown?site_id={site}&period=30d&property=event:page&metrics=visitors&limit=10",
    headers: [],
    params: [{ name: "site", label: "Site", placeholder: "example.com", required: true, account: true }],
    extract: { mode: "list", path: "results", label: "page", value: "visitors" },
    every: 3600,
    docs: "https://plausible.io/docs/stats-api-v1#get-apiv1statsbreakdown",
    title: "Top pages",
    stream: "top-pages",
  },
  {
    id: "plausible-sources",
    label: "Where visitors came from, last 30 days",
    provider: "Plausible",
    service: "plausible",
    blurb: "The sites and searches that sent you visitors, with how many each sent.",
    widget: "list",
    url: "https://plausible.io/api/v1/stats/breakdown?site_id={site}&period=30d&property=visit:source&metrics=visitors&limit=10",
    headers: [],
    params: [{ name: "site", label: "Site", placeholder: "example.com", required: true, account: true }],
    extract: { mode: "list", path: "results", label: "source", value: "visitors" },
    every: 3600,
    docs: "https://plausible.io/docs/stats-api-v1#get-apiv1statsbreakdown",
    title: "Where they came from",
    stream: "referrers",
  },
  {
    id: "beehiiv-subs",
    label: "Active subscribers",
    provider: "beehiiv",
    service: "beehiiv",
    blurb: "Subscribers on one publication.",
    widget: "metric",
    url: "https://api.beehiiv.com/v2/publications/{pub}?expand[]=stats",
    headers: [],
    params: [{ name: "pub", label: "Publication", placeholder: "pub_…", required: true, account: true }],
    extract: { mode: "value", path: "data.stats.active_subscriptions" },
    every: 3600,
    docs: "https://developers.beehiiv.com/api-reference/publications/show",
    title: "Subscribers",
  },
  {
    id: "buttondown-subs",
    label: "Subscribers",
    provider: "Buttondown",
    service: "buttondown",
    blurb: "Everyone on your newsletter.",
    widget: "metric",
    url: "https://api.buttondown.com/v1/subscribers?type=regular",
    headers: [],
    params: [],
    extract: { mode: "value", path: "count" },
    every: 3600,
    docs: "https://docs.buttondown.com/api-subscribers-list",
    title: "Subscribers",
  },
  {
    id: "youtube-subs",
    label: "Channel subscribers",
    provider: "YouTube",
    service: "youtube",
    blurb: "Subscriber count for a YouTube channel.",
    widget: "metric",
    // the key goes in this address only as the request goes out, so it is never kept in it
    url: "https://www.googleapis.com/youtube/v3/channels?part=statistics&id={channel}",
    headers: [],
    params: [{ name: "channel", label: "Channel ID", placeholder: "UC…", required: true }],
    extract: { mode: "value", path: "items[0].statistics.subscriberCount" },
    every: 3600,
    docs: "https://developers.google.com/youtube/v3/docs/channels/list",
    title: "YouTube subscribers",
  },
  {
    id: "github-private",
    label: "Repository (private)",
    provider: "GitHub",
    service: "github",
    blurb: "Open issues on any repo you can see, with your own token.",
    widget: "metric",
    url: "https://api.github.com/repos/{repo}",
    headers: [],
    params: [{ name: "repo", label: "Repository", placeholder: "owner/name", required: true }],
    extract: { mode: "value", path: "open_issues_count" },
    every: 1800,
    docs: "https://docs.github.com/en/rest/repos/repos#get-a-repository",
    title: "Open issues",
  },
];

export const RECIPE: Record<string, RecipeSpec> = Object.fromEntries(RECIPES.map((r) => [r.id, r]));

/**
 * The template with its params filled in. In an address each value stays the
 * one part of it that it fills: a value that would step out of the endpoint
 * (a . or .. part, or a slash where one part goes) is not filled in at all,
 * so the address can never read anything but what its recipe says.
 */
export function fillTemplate(s: string, params: Record<string, string>): string {
  const query = s.indexOf("?");
  return s.replace(/\{(\w+)\}/g, (m, k: string, at: number) => {
    if (!Object.hasOwn(params, k)) return m;
    const v = String(params[k]);
    // header values are not URLs: leave them as typed
    if (!s.startsWith("http")) return v;
    if (query >= 0 && at > query) return encodeURIComponent(v);
    if (pathProblem(m, v)) return m;
    // a repo like owner/name is two parts of the path, and keeps its one slash
    return m === "{repo}" ? v.split("/").map(encodeURIComponent).join("/") : encodeURIComponent(v);
  });
}

/** A part of an address's path that would not stay where it is written: a . or .. part, an empty one, or a slash where one part goes. */
function pathProblem(token: string, v: string): boolean {
  const parts = token === "{repo}" ? v.split("/") : [v];
  if (token === "{repo}" && parts.length !== 2) return true;
  return parts.some((p) => !p || p === "." || p === ".." || /[\\/]/.test(p));
}

/**
 * Why a recipe's params would not stay inside its endpoint, in words for the
 * agent that gave them, or null when every one does.
 */
export function paramProblem(template: string, params: Record<string, string>): string | null {
  const query = template.indexOf("?");
  for (const m of template.matchAll(/\{(\w+)\}/g)) {
    const k = m[1];
    if (!Object.hasOwn(params, k) || (query >= 0 && (m.index ?? 0) > query)) continue;
    const v = String(params[k]);
    if (!pathProblem(m[0], v)) continue;
    return k === "repo"
      ? `params.repo must be one repository written as owner/name, with no other slash and no . or .. part. "${v.slice(0, 80)}" is not.`
      : `params.${k} must be one ${k}, with no slash and not . or .., so that it reads only what its recipe reads. "${v.slice(0, 80)}" is not.`;
  }
  return null;
}
